import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import type { NumericValue } from '@/lib/types/inputNumber';

// Props interface matching original exactly
interface Props {
	value: NumericValue;
	min?: number;
	max?: number;
	controls?: boolean;
	disabled?: boolean;
	precision?: number;
	step?: number;
}

interface Emits {
	(e: 'input', value: NumericValue): void;
	(e: 'change'): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ElInputNumberWrapper',
  props: {
    value: null,
    min: { default: 0 },
    max: { default: Infinity },
    controls: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    precision: { default: 0 },
    step: { default: 1 }
  },
  emits: ["input", "change"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'input', value: NumericValue): void;
	(e: 'change'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: NumericValue, min: number, max: number, controls: boolean, disabled: boolean, precision: number, step: number };



// Emits matching original


// Expose props for v-bind (exact replication of original)
const { min, max, controls, disabled, precision, step } = props;

// Utility function to quantize value to step
const quantizeToStep = (value: number, step: number, min: number): number => {
	// Ensure value is a valid number (handle string numbers from API)
	const numericValue = typeof value === 'string' ? parseFloat(value) : value;

	// Return NaN if value is invalid
	if (isNaN(numericValue) || isNaN(step) || step === 0) {
		console.warn(
			`[ElInputNumberWrapper] Invalid numeric values: value=${value}, step=${step}, min=${min}`,
		);
		return NaN;
	}

	// Handle special cases with Infinity
	if (!isFinite(min) || !isFinite(numericValue)) {
		// If min is infinite or value is infinite, no quantization needed
		return numericValue;
	}

	// Calculate how many steps from min
	const stepsFromMin = Math.round((numericValue - min) / step);
	// Return quantized value
	const quantized = min + stepsFromMin * step;

	// Ensure result is finite
	return isFinite(quantized) ? quantized : numericValue;
};

// Computed v-model implementation with step quantization
const inputVal = computed({
	get(): number | undefined {
		if (props.value === null || props.value === undefined) {
			return undefined;
		}

		// Handle string numbers from API data
		if (typeof props.value === 'string') {
			const parsed = parseFloat(props.value);
			return isNaN(parsed) ? undefined : parsed;
		}

		return props.value as number | undefined;
	},
	set(value: number | undefined): void {
		if (value === undefined) {
			emit('input', null);
		} else {
			// Quantize value to be a multiple of step
			const quantizedValue = quantizeToStep(value, props.step, props.min);

			// Handle NaN result from quantization
			if (isNaN(quantizedValue)) {
				emit('input', undefined);
				return;
			}

			// Log quantization in development
			if (process.env.NODE_ENV === 'development' && quantizedValue !== value) {
				console.log(
					`[ElInputNumberWrapper] Step quantization: ${value} → ${quantizedValue} (step: ${props.step})`,
				);
			}

			emit('input', quantizedValue);
		}
	},
});

// Change handler (exact from original)
const changedValue = (): void => {
	emit('change');
};

return { __sfc: true,props, emit, min, max, controls, disabled, precision, step, quantizeToStep, inputVal, changedValue }
}

})